/**
 * clipboard.ts — pure copy/paste transforms for graph nodes + edges.
 *
 * The App owns the clipboard STATE (what's copied, how many times it has been
 * pasted); this module owns the TRANSFORMS: capturing a clone that later graph
 * edits can't mutate, and minting pasteable copies with fresh ids, offset
 * positions, and remapped edge endpoints. Edges between a copied node and a
 * node OUTSIDE the selection are dropped — a pasted edge must not dangle.
 *
 * Id minting mirrors LocalAdapter.duplicateNode's scheme (`<id>_copy`,
 * `<id>_copy2`, …) so duplicate and paste read as the same family of action.
 */

import type { Bend, GraphEdge, GraphNode } from '../engine/types';

/** How far each paste offsets from the source position (grows per paste so
 *  repeated pastes fan out instead of stacking exactly on top of each other). */
export const PASTE_OFFSET = 40;

export interface GraphClipboard {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

/** Clone the selection into a self-contained clipboard entry. Deep-clones so
 *  subsequent edits to the live graph never bleed into the clipboard. */
export function captureClipboard(nodes: GraphNode[], edges: GraphEdge[]): GraphClipboard {
  const ids = new Set(nodes.map((n) => n.id));
  return {
    nodes: structuredClone(nodes),
    // Internal edges only (both endpoints selected) — see the file header.
    edges: structuredClone(edges.filter((e) => ids.has(e.source) && ids.has(e.target))),
  };
}

/** The `duplicateNode` id scheme: bare, then `_copy`, `_copy2`, … */
function uniqueId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  const first = `${base}_copy`;
  if (!taken.has(first)) return first;
  let n = 2;
  while (taken.has(`${first}${n}`)) n += 1;
  return `${first}${n}`;
}

/** Mint pasteable copies: fresh unique ids, remapped edge endpoints, positions
 *  (and bend points) offset by `PASTE_OFFSET × (pasteCount + 1)`.
 *  `occupiedIds` is every node/edge id already in the target graph (plus ids
 *  minted earlier in this same paste batch). */
export function pasteClipboard(
  clip: GraphClipboard,
  occupiedIds: Iterable<string>,
  pasteCount: number,
): GraphClipboard {
  const taken = new Set(occupiedIds);
  const d = PASTE_OFFSET * (pasteCount + 1);
  const offsetPoint = (p: { x: number; y: number }) => ({ x: p.x + d, y: p.y + d });

  const idMap = new Map<string, string>();
  const nodes = clip.nodes.map((n) => {
    const id = uniqueId(n.id, taken);
    taken.add(id);
    idMap.set(n.id, id);
    return {
      ...structuredClone(n),
      id,
      position: n.position ? offsetPoint(n.position) : undefined,
    };
  });

  const edges = clip.edges.map((e) => {
    const source = idMap.get(e.source)!;
    const target = idMap.get(e.target)!;
    const id = uniqueId(`${source}__${target}`, taken);
    taken.add(id);
    return {
      ...structuredClone(e),
      id,
      source,
      target,
      bends: e.bends?.map((b: Bend) => ({ ...b, ...offsetPoint(b) })),
    };
  });

  return { nodes, edges };
}
